import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { GraphQLClient } from "graphql-request";

import Header from "../../components/Header";
import ActionButton from "../../components/ActionButton";
import ThemedDataGrid from "../../components/ThemedDataGrid";
import { useTenant } from "../../context/TenantContext";
import { useSnackbar } from "../../components/snackbarProvider.jsx";

import { graphqlEndpoint } from "../../config";
import { UserRole } from "../../constants/userRoles.ts";
import { READ_TENANT_USERS_BY_TENANT } from "../../graph/tenantUsers/queries";
import { READ_TENANT_LEAVE_YEARS } from "../../graph/holidayManagement/queries";

import ManageHolidayRequests from "./requests";

const client = new GraphQLClient(graphqlEndpoint);

const allowedTenantRoles = [
  UserRole.TENANT_ADMIN,
  UserRole.TENANT_STAFF,
];

const ManageStaffWorkPatterns = () => {
  const navigate = useNavigate();
  const { tenantId, tenantName } = useTenant();
  const { setSnackbar } = useSnackbar();

  const [viewMode, setViewMode] = useState("requests");
  const [staff, setStaff] = useState([]);
  const [leaveYears, setLeaveYears] = useState([]);
  const [selectedLeaveYearID, setSelectedLeaveYearID] = useState("");
  const [loading, setLoading] = useState(false);

  const handleViewModeChange = (_event, nextViewMode) => {
    if (nextViewMode !== null) {
      setViewMode(nextViewMode);
    }
  };

  const fetchData = useCallback(async () => {
    if (!tenantId) {
      setStaff([]);
      setLeaveYears([]);
      setSelectedLeaveYearID("");
      return;
    }

    setLoading(true);

    try {
      const [usersData, leaveYearsData] = await Promise.all([
        client.request(READ_TENANT_USERS_BY_TENANT, { tenantId }),
        client.request(READ_TENANT_LEAVE_YEARS, { tenantID: tenantId }),
      ]);

      const staffRows = (usersData.readTenantUsersByTenant || [])
        .filter(
          (tenantUser) =>
            !tenantUser.archivedAt &&
            allowedTenantRoles.includes(tenantUser.user?.role)
        )
        .map((tenantUser) => {
          const globalUser = tenantUser.user;
          const fallbackName =
            `${globalUser?.firstName || ""} ${globalUser?.lastName || ""}`.trim();

          return {
            // Work patterns use the tenant membership ID.
            id: tenantUser.id,
            name:
              tenantUser.displayName ||
              fallbackName ||
              globalUser?.email ||
              tenantUser.userId,
            email: globalUser?.email || "",
            role: globalUser?.role || "",
          };
        });

      const openLeaveYears = (leaveYearsData.readTenantLeaveYears || [])
        .filter((leaveYear) => leaveYear.status === "OPEN")
        .sort((a, b) => a.startDate.localeCompare(b.startDate));

      setStaff(staffRows);
      setLeaveYears(openLeaveYears);
      setSelectedLeaveYearID((currentID) => {
        if (openLeaveYears.some((leaveYear) => leaveYear.id === currentID)) {
          return currentID;
        }

        return openLeaveYears[0]?.id || "";
      });
    } catch (err) {
      console.error("Failed to load staff work pattern options:", err);
      setStaff([]);
      setLeaveYears([]);
      setSelectedLeaveYearID("");

      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to load staff and leave years",
      });
    } finally {
      setLoading(false);
    }
  }, [tenantId, setSnackbar]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleManagePattern = (staffMember) => {
    if (!selectedLeaveYearID) {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Select an open leave year first",
      });
      return;
    }

    navigate(
      `/manageStaffWorkPatterns/edit/${staffMember.id}?leaveYearID=${encodeURIComponent(
        selectedLeaveYearID
      )}`,
      {
        state: {
          tenantID: tenantId,
          staffTenantUserID: staffMember.id,
          staffName: staffMember.name,
          leaveYearID: selectedLeaveYearID,
          redirectPath: "/manageStaffWorkPatterns",
        },
      }
    );
  };

  const handleCreatePattern = () => {
    if (!selectedLeaveYearID) {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Create an open leave year before adding work patterns",
      });
      return;
    }

    navigate("/createStaffWorkPattern", {
      state: {
        tenantID: tenantId,
        leaveYearID: selectedLeaveYearID,
        redirectPath: "/manageStaffWorkPatterns",
      },
    });
  };

  const formatRole = (role) => {
    if (role === UserRole.TENANT_ADMIN) return "Tenant Admin";
    if (role === UserRole.TENANT_STAFF) return "Tenant Staff";
    return role || "Unknown";
  };

  const columns = [
    {
      field: "name",
      headerName: "Name",
      flex: 1.2,
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1.4,
    },
    {
      field: "role",
      headerName: "Role",
      flex: 1,
      valueGetter: (value) => formatRole(value),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <Button
          size="small"
          variant="outlined"
          disabled={!selectedLeaveYearID}
          onClick={() => handleManagePattern(row)}
        >
          Manage Pattern
        </Button>
      ),
    },
  ];

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        gap={2}
        mb={2}
      >
        <Header
          title="HOLIDAY MANAGEMENT"
          subtitle={
            tenantName
              ? `Manage holiday requests and staff patterns for ${tenantName}`
              : "Manage staff holiday requests and work patterns"
          }
        />

        {viewMode === "patterns" && (
          <ActionButton
            tone="gold"
            onClick={handleCreatePattern}
            disabled={!tenantId || !selectedLeaveYearID}
          >
            Create Work Pattern
          </ActionButton>
        )}
      </Box>

      <Box mb={2}>
        <ToggleButtonGroup
          exclusive
          value={viewMode}
          onChange={handleViewModeChange}
          size="small"
        >
          <ToggleButton value="requests">Holiday Requests</ToggleButton>
          <ToggleButton value="patterns">Staff Work Patterns</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {viewMode === "requests" ? (
        <ManageHolidayRequests />
      ) : !tenantId ? (
        <Box mt={4}>
          <Typography variant="h6">
            Select a tenant in the top bar to view staff work patterns.
          </Typography>
        </Box>
      ) : (
        <>
          <FormControl size="small" sx={{ minWidth: 280, mb: 2 }}>
            <InputLabel id="staff-work-pattern-leave-year-label">
              Leave Year
            </InputLabel>

            <Select
              labelId="staff-work-pattern-leave-year-label"
              value={selectedLeaveYearID}
              label="Leave Year"
              onChange={(event) =>
                setSelectedLeaveYearID(event.target.value)
              }
            >
              {leaveYears.map((leaveYear) => (
                <MenuItem key={leaveYear.id} value={leaveYear.id}>
                  {leaveYear.startDate} to {leaveYear.endDate}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {loading ? (
            <Typography>Loading staff and leave years...</Typography>
          ) : leaveYears.length === 0 ? (
            <Typography mt={2}>
              This tenant has no open leave year. Create one before managing
              staff work patterns.
            </Typography>
          ) : (
            <ThemedDataGrid rows={staff} columns={columns} />
          )}
        </>
      )}
    </Box>
  );
};

export default ManageStaffWorkPatterns;